import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { AdditionalSettingsModalMobilePortal } from '../additional-settings-modal';
import { BalanceLoader } from '../balance-loader/balance-loader';
import { EmptyWalletSection } from '../empty-wallet-section/empty-wallet-section';
import { SearchIcon, SwordsIcon } from 'lucide-react';

import MobileRoomLayout from 'shared/components/layout/mobile-layout';
import AnimatedTransition from 'shared/components/ui/AnimatedTransition/AnimatedTransition';
import GuysLoader from 'shared/components/ui/Loaders/GuysLoader/GuysLoader';
import PrimaryButton from 'shared/components/ui/PrimaryButton/PrimaryButton';
import SecondaryBtn from 'shared/components/ui/SecondaryBtn/SecondaryBtn';
import Select from 'shared/components/ui/Select/Select';
import BetInput from 'shared/components/ui/bet-input/bet-input';
import { OptimizedImage } from 'shared/components/ui/optimized-image/optimized-image';
import { RoomFooterText } from 'shared/components/ui/room-footer-text/room-footer-text';
import { Switch } from 'shared/components/ui/switch/switch';
import { allowanceMessages, creatingMessages } from 'shared/constants/text-loaders';
import { useAccount } from 'shared/hooks/use-web3';
import { useGlobalStore } from 'shared/store/globalStore';
import { prettyTx } from 'shared/utils/pretty-tx';
import { formatNumber } from 'shared/web3/utils';
import { CurrencyType } from 'shared/web3/utils/getCurrencyInfo';

import floor from 'assets/png/floor.png?format=webp;avif&as=picture';
import SettingsIcon from 'assets/settings.svg?react';

import {
  getBox,
  getOptionsWithBalances,
  getSelectedOptionWithBalance,
} from './create-game-section.utils';
import { useCreateGameSection } from './useCreateGameSection';

interface ICreateGameSectionMobileProps {
  openLiveGames: () => void;
  approveAllowanceHandler: (fullAllowance: boolean, bet: number) => void;
  startGameHandler: (bet: number, wins: number, playerB: string, currency?: CurrencyType) => void;
}

export const CreateGameSectionMobile: FC<ICreateGameSectionMobileProps> = ({
  openLiveGames,
  approveAllowanceHandler,
  startGameHandler,
}) => {
  const { t } = useTranslation();

  const {
    bet,
    selectedCurrency,
    selectedBalance,
    isApproving,
    getAuthorizeDisable,
    fullAllowance,
    betIsValid,
    currentAllowance,
    isFetching,
    isStarting,
    txHash,
    betError,
    currencyForSelect,
    isModalOpened,
    balances,
    isLoadingBalances,
    handleSelectCurrency,
    handleBetClick,
    setFullAllowance,
    handleSetBetInput,
    modalOpenHandler,
    handleStartGame,
    handleApprove,
  } = useCreateGameSection({
    approveAllowanceHandler,
    startGameHandler,
  });

  const [searchParams] = useSearchParams();
  const { address } = useAccount();
  const leaderboardMap = useGlobalStore((state) => state.leaderboardMap);
  const optionsWithBalances = useMemo(
    () => getOptionsWithBalances(currencyForSelect, balances),
    [currencyForSelect, balances],
  );

  const selectedOptionWithBalance = useMemo(() => {
    if (!selectedCurrency) return null;
    return getSelectedOptionWithBalance({
      value: selectedCurrency.value,
      symbol: selectedCurrency.symbol,
    });
  }, [selectedCurrency]);

  const boxImage = useMemo(
    () => getBox({ balance: selectedBalance, bet, betThresholds: selectedCurrency?.bet }),
    [selectedBalance, bet, selectedCurrency],
  );

  const isStartGameDisabled = useMemo(
    () =>
      !bet ||
      !betIsValid ||
      Number(currentAllowance) < +bet ||
      isApproving ||
      isFetching ||
      isStarting ||
      (selectedBalance ?? 0) < +bet,
    [isApproving, isFetching, betIsValid, currentAllowance, bet, selectedBalance, isStarting],
  );

  const isLoading = useMemo(() => isLoadingBalances || isFetching, [isLoadingBalances, isFetching]);

  const shouldShowWalletWarning = useMemo(
    () => (selectedBalance <= 0 || +bet > selectedBalance) && !isLoading,
    [selectedBalance, bet, isLoading],
  );

  const shouldShowActionButtons = useMemo(
    () => selectedBalance > 0 && !isLoading && selectedBalance >= +bet,
    [selectedBalance, isLoading, bet],
  );

  const screenAddress = useMemo(
    () => prettyTx({ address: searchParams.get('invite'), leaderboardMap }),
    [searchParams.get('invite'), leaderboardMap],
  );

  return (
    <MobileRoomLayout
      title={t('modals.setBet.title')}
      footer={
        <div className="z-50 flex w-full flex-col items-center justify-between gap-1 bg-grape">
          {isLoading || !selectedCurrency ? (
            <BalanceLoader />
          ) : (
            <>
              {shouldShowActionButtons && (
                <div className="grid w-full grid-cols-2 gap-2">
                  <PrimaryButton
                    text="common.authorize"
                    disabled={getAuthorizeDisable}
                    onClick={handleApprove}
                    isMobile
                  />
                  <PrimaryButton
                    text="common.startGame"
                    disabled={isStartGameDisabled}
                    onClick={handleStartGame}
                    isMobile
                  />
                </div>
              )}
            </>
          )}
          <RoomFooterText />
        </div>
      }
      showFloor={false}
    >
      <>
        <div className="h-full flex-grow pt-4">
          {isLoading || !selectedCurrency ? (
            <BalanceLoader />
          ) : (
            <>
              <AnimatedTransition show={isApproving || isStarting}>
                <GuysLoader
                  hash={txHash}
                  messages={isApproving ? allowanceMessages : creatingMessages}
                  className="absolute left-0 top-0 z-50 h-full w-full rounded-md bg-[#000014D9] bg-opacity-85 sm:rounded-[14px]"
                />
              </AnimatedTransition>
              <div className="relative flex h-full w-full flex-col">
                <SecondaryBtn
                  text="pages.liveGamesSection.find"
                  svg={<SearchIcon className="h-4 w-4" color="#FFECC0" />}
                  onClick={openLiveGames}
                  className="mb-6 min-h-7 w-full rounded-3xl"
                  childClassName="rounded-3xl text-sm py-1.5 min-h-7 leading-4"
                />
                <div className="gradient-title mb-3 flex items-center justify-between text-[14px] font-bold">
                  {t('modals.setBet.select')}

                  <button
                    className="rounded-[10px] border border-indigo bg-border p-1"
                    onClick={modalOpenHandler}
                    data-testid="settings-button-mobile"
                  >
                    <SettingsIcon />
                  </button>
                </div>
                <div className="mb-[0.875rem] flex w-full items-start justify-between border-b border-border pb-1.5">
                  <div className="mr-2.5 flex w-[70%] flex-col sm:mr-[1.375rem] sm:flex-grow">
                    <BetInput
                      value={bet.toString()}
                      valueHandler={handleSetBetInput}
                      type="number"
                      className="sm:flex-grow"
                      balance={selectedBalance ?? 0}
                      disabled={isLoading || isApproving}
                      betError={betError}
                      selectedCurrency={selectedCurrency}
                    />
                  </div>
                  <Select
                    className="flex-grow"
                    options={optionsWithBalances}
                    selectedOption={selectedOptionWithBalance}
                    onSelect={handleSelectCurrency}
                    disabled={isLoading || isApproving}
                  />
                </div>
                {selectedBalance > 0 && (
                  <div className="flex flex-wrap items-center justify-between pb-5">
                    <p className="text-sm text-simple-text">{t('modals.setBet.popular')}</p>
                    <div className="item-center flex justify-end gap-2">
                      {selectedCurrency?.bet &&
                        selectedCurrency.bet.map((betValue) => (
                          <button
                            key={betValue}
                            className="min-w-16 rounded-lg border border-indigo bg-[linear-gradient(91deg,_#05055B_-6.69%,_#151552_106.59%)] px-2 py-[7px] text-md text-light-grape disabled:opacity-50 sm:py-2.5"
                            onClick={() => handleBetClick(betValue)}
                            disabled={isApproving || isFetching}
                          >
                            {formatNumber(+betValue)} {selectedCurrency.symbol}
                          </button>
                        ))}
                    </div>
                  </div>
                )}
                {searchParams.get('invite') && address !== searchParams.get('invite') && (
                  <div className="mb-5 rounded-xl bg-gradient-to-r from-[#3BC587] to-[#399197] p-[1px]">
                    <div className="flex items-center gap-2 rounded-xl bg-[linear-gradient(91deg,_#05055B_-6.69%,_#074540_106.59%)] px-3 py-2.5">
                      <SwordsIcon className="h-[1.25rem] w-[1.25rem] text-[#3CD2C7]/75" />
                      <p className="text-xs text-[#3CD2C7]/75">
                        {t('createGameSection.invited', { player: screenAddress })}
                      </p>
                    </div>
                  </div>
                )}
                {!shouldShowWalletWarning && (
                  <div className="relative flex flex-grow flex-col items-center justify-end gap-4 pb-5">
                    {boxImage}
                    <AnimatedTransition asFragment show={!getAuthorizeDisable}>
                      <div className="z-50 flex">
                        <p className="mr-4 border-r border-border pr-4 text-sm leading-[14px] text-light-gray">
                          {t('modals.setBet.plan')}
                        </p>
                        <Switch
                          className="ml-2"
                          onCheckedChange={(checked) => setFullAllowance(checked)}
                          checked={fullAllowance}
                        />
                      </div>
                    </AnimatedTransition>
                    <AnimatedTransition asFragment show={getAuthorizeDisable}>
                      <div className="z-50 flex">
                        <p className="text-sm text-light-gray">
                          {t('modals.setBet.case')}
                          <span className="text-yellow">{t('modals.setBet.earn2x')}</span>
                          {t('modals.setBet.case2')}
                        </p>
                      </div>
                    </AnimatedTransition>
                    <OptimizedImage
                      imageData={floor}
                      alt="floor"
                      className="absolute bottom-0 left-1/2 z-0 min-w-[70rem] -translate-x-1/2 transform"
                    />
                  </div>
                )}
                {shouldShowWalletWarning && <EmptyWalletSection />}
              </div>
            </>
          )}
        </div>
        <AdditionalSettingsModalMobilePortal isOpen={isModalOpened} onClose={modalOpenHandler} />
      </>
    </MobileRoomLayout>
  );
};
